import FollowProfileButton from "@/components/common/follow-profile-button";
import ProfilePicture from "@/components/common/profile-picture";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatImageUrl } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { ChevronRightIcon, VerifiedIcon } from "lucide-react";
import Link from "next/link";

type ProfileTooltipProps = {
	image?: string;
	isFounder?: boolean | null;
	isVerified?: boolean | null;
	title: string | null;
	description: string;
	username?: string;
	isFollowing?: boolean | null;
	hideFollowButton?: boolean;
};

const ProfileTooltip = ({
	image,
	isFounder,
	isVerified,
	title,
	description,
	username,
	isFollowing,
	hideFollowButton = false,
}: ProfileTooltipProps) => {
	const { isLoggedIn } = useAuth();
	const profilePicture = formatImageUrl(image);

	return (
		<TooltipProvider>
			<Tooltip delayDuration={0}>
				<TooltipTrigger type="button" className="mx-auto outline-gray-400 text-gray-800">
					<ProfilePicture
						alternateText={title}
						isFounder={isFounder}
						isVerified={isVerified}
						profilePictureUrl={profilePicture}
						className={{
							container: "mx-auto h-10 w-10 rounded-full p-0.5",
							image: "rounded-full text-gray-600",
							badgeImage: "-bottom-3 h-6 w-6",
						}}
					/>
				</TooltipTrigger>

				<TooltipContent className="tooltip-content max-w-sm rounded-lg bg-white text-gray-700 shadow">
					<div className="flex w-max items-center gap-4 px-4 py-2">
						<ProfilePicture
							alternateText={title}
							isFounder={isFounder}
							isVerified={isVerified}
							profilePictureUrl={profilePicture}
							className={{
								container: "mx-auto h-10 w-10 rounded-full p-0.5",
								image: "rounded-full text-gray-600",
								badgeImage: "-bottom-3 h-6 w-6",
							}}
						/>

						<div className="text-left">
							<div className="flex items-center gap-1">
								<h3 className="text-base font-semibold">{title}</h3>

								{isFounder ? <VerifiedIcon className="h-4 w-4 text-brand-primary" /> : null}

								{!hideFollowButton && (
									<>
										<div className="h-1 w-1 rounded-full bg-brand-primary" />

										{username && (
											<FollowProfileButton
												username={username}
												isFollowing={isLoggedIn && isFollowing ? true : false}
												className="m-0 w-fit bg-transparent p-0 text-xs text-brand-primary md:p-0"
											/>
										)}
									</>
								)}
							</div>

							<span className="mt-1 block text-xs truncate text-gray-600">{description}</span>

							<Link
								href={`/${username}`}
								className="mt-3 flex items-center justify-start gap-1 text-brand-primary"
							>
								<span className="text-xs font-medium">Visit Profile</span>
								<ChevronRightIcon className="h-3 w-3" />
							</Link>
						</div>
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
};

export default ProfileTooltip;
